import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { v4 as uuidv4 } from 'uuid';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMemo } from "react";
import { DollarSign, Users, Percent, AlertTriangle } from "lucide-react";
import { RentalWithRelations, ShiftWithRelations } from "@/types/models";
import { getConfiguration, uploadFile, getCurrentExchangeRate, getActiveRentalsLigth } from "@/integrations/supabase/services";
import { createShiftPayment, closeShift } from "@/integrations/supabase/shift-services";
import { getBanks } from "@/integrations/supabase/bank-services";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import FileUpload from "@/components/common/FileUpload";
import { useAuth } from "@/context/AuthContext";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { formatDateFull, getShiftPercentages } from "@/lib/utils";

interface CloseShiftModalProps {
  shift: ShiftWithRelations | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  completedRentals: RentalWithRelations[];
  rentalsLoading: boolean;
}

const paymentSchema = z.object({
  bank_id: z.coerce.number().min(1, "Debe seleccionar un banco"),
  amount: z.coerce.number().min(0.01, "El monto debe ser mayor a 0"),
  reference: z.string().min(1, "La referencia es requerida"),
});

type PaymentFormValues = z.infer<typeof paymentSchema>;

const CloseShiftModal = ({ shift, isOpen, onClose, onSuccess, completedRentals, rentalsLoading }: CloseShiftModalProps) => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [isClosing, setIsClosing] = useState(false);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [hasDiscrepancy, setHasDiscrepancy] = useState(false);
  
  const { data: currentRate } = useQuery({
    queryKey: ['currentExchangeRate'],
    queryFn: getCurrentExchangeRate,
  });

  const { data: banks = [], isLoading: banksLoading } = useQuery({
    queryKey: ['banks'],
    queryFn: getBanks,
    enabled: isOpen,
  });

  const { data: activeRentals = [] } = useQuery({
    queryKey: ['active-rentals', shift?.id],
    queryFn: () => shift ? getActiveRentalsLigth(shift.operator_id, shift.start_time) : Promise.resolve([]),
    enabled: isOpen && !!shift,
  });

  const { data: configuration, isLoading: configLoading } = useQuery({
    queryKey: ['configuration'],
    queryFn: getConfiguration,
    enabled: isOpen,
  });

  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      bank_id: 0,
      amount: 0,
      reference: "",
    },
  });

  const watchedAmount = form.watch("amount");
  const exchangeRate = currentRate && typeof currentRate === 'object' && 'usd_to_bs' in currentRate ? currentRate.usd_to_bs : 0;
  const amountInBs = watchedAmount && exchangeRate ? Math.ceil(watchedAmount * exchangeRate - 0.00001) : 0;

  const operatorAmountInBs = useMemo(() => 
    shift?.operator_amount && exchangeRate ? Math.ceil(shift.operator_amount * exchangeRate) : 0,
    [shift?.operator_amount, exchangeRate]
  );
  const adminAmountInBs = useMemo(() =>
    shift?.amount_admin && exchangeRate ? Math.ceil(shift.amount_admin * exchangeRate) : 0,
    [shift?.amount_admin, exchangeRate]
  );

  const { operatorPercentage: operatorPercent, administratorPercentage: adminPercent } = useMemo(() => {
    return getShiftPercentages(
      shift,
      configuration?.operator_percentage ?? 25,
      configuration?.administrator_percentage ?? 75
    );
  }, [shift, configuration]);

  const closeShiftMutation = useMutation({
    mutationFn: async ({ shiftId, completedRentals, paymentData, receiptUrl }: { 
      shiftId: string; 
      completedRentals: number;
      paymentData?: PaymentFormValues & { exchange_rate: number };
      receiptUrl?: string;
    }) => {
      const closedShift = await closeShift(shiftId, completedRentals);
      
      // If payment data is provided, create the payment record
      if (paymentData && receiptUrl) {
        const branchId = profile.branch_id || shift?.operator?.branch_id || shift?.operator?.branch?.id;

        if (!branchId) {
          throw new Error("No se pudo determinar la sucursal para el pago");
        }

        await createShiftPayment({
          shift_id: shiftId,
          operator_id: profile.id,
          branch_id: branchId,
          bank_id: paymentData.bank_id,
          amount: paymentData.amount,
          reference: paymentData.reference,
          exchange_rate: paymentData.exchange_rate,
          receipt_photo_url: receiptUrl,
        });
      }
      return closedShift;
    },
    onSuccess: () => {
      toast({
        title: "Jornada cerrada",
        description: showPaymentForm ? "La jornada se ha cerrado y el pago ha sido registrado exitosamente" : "La jornada se ha cerrado exitosamente",
      });
      onSuccess();
      onClose();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Error al cerrar la jornada",
        variant: "destructive",
      });
    },
  });

  const hasActiveRentals = activeRentals && activeRentals.length > 0;

  const verifiedTotalCollected = useMemo(() => {
    if (!completedRentals) return 0;
    return completedRentals.reduce((acc, rental) => acc + (rental.total_amount || 0), 0);
  }, [completedRentals]);

  useEffect(() => {
    if (shift && completedRentals) {
      // Compare with a small tolerance for floating point issues
      const discrepancy = Math.abs((shift.total_collected || 0) - verifiedTotalCollected) > 0.01;
      setHasDiscrepancy(discrepancy);
    }
  }, [shift, completedRentals, verifiedTotalCollected]);

  useEffect(() => {
    if (showPaymentForm && shift?.amount_admin) {
      form.setValue("amount", Number(shift.amount_admin.toFixed(2)));
    }
  }, [showPaymentForm, shift, form]);

  const handleCloseShift = async (data?: PaymentFormValues) => {
    if (!shift?.id || !configuration) return;
    
    setIsClosing(true);
    try {
      let receiptUrl;
      
      // If showing payment form, validate and upload receipt
      if (showPaymentForm) {
        if (!data) {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Debe completar la información de pago",
          });
          return;
        }
        
        if (!receiptFile) {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Debe subir el comprobante de pago",
          });
          return;
        }

        if (!exchangeRate) {
          toast({
            variant: "destructive",
            title: "Error",
            description: "No se pudo obtener la tasa de cambio",
          });
          return;
        }

        // Convert file to base64 and upload
        const fileReader = new FileReader();
        const base64String = await new Promise<string>((resolve) => {
          fileReader.onload = () => {
            const result = fileReader.result as string;
            resolve(result);
          };
          fileReader.readAsDataURL(receiptFile);
        });

        const filePath = `receipts/${uuidv4()}.jpg`;
        receiptUrl = await uploadFile(base64String, filePath);
      }

      await closeShiftMutation.mutateAsync({
        shiftId: shift.id,
        completedRentals: completedRentals.length,
        paymentData: data ? { ...data, exchange_rate: exchangeRate } : undefined,
        receiptUrl,
      });
    } finally {
      setIsClosing(false);
    }
  };

  const handleClose = () => {
    setShowPaymentForm(false);
    setReceiptFile(null);
    form.reset();
    onClose();
  };

  if (!shift) return null;

  // Show loading state while data is being fetched
  if (configLoading || rentalsLoading || banksLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cerrar Jornada</DialogTitle>
            <DialogDescription>
              Cargando información de la jornada...
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // If configuration is still not loaded, show error
  if (!configuration) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription>
              No se pudo cargar la configuración del sistema.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={handleClose}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cerrar Jornada</DialogTitle>
          <DialogDescription>
            Cerrar la jornada de {formatDateFull(shift.start_time)}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {hasDiscrepancy && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>¡Discrepancia en los Totales!</AlertTitle>
              <AlertDescription>
                <p className="font-semibold">El total recaudado no coincide con la suma de los alquileres completados.</p>
                <ul className="list-disc pl-5 mt-2">
                  <li>Total Acumulado en Jornada: <strong>${shift.total_collected?.toFixed(2) || '0.00'}</strong></li>
                  <li>Total Verificado de Alquileres: <strong>${verifiedTotalCollected.toFixed(2)}</strong></li>
                </ul>
                <p className="mt-2">Se recomienda revisar los alquileres antes de cerrar. Puede continuar si lo desea.</p>
              </AlertDescription>
            </Alert>
          )}

          {hasActiveRentals && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Alquileres activos</AlertTitle>
              <AlertDescription>
                <p className="font-semibold">No se puede cerrar la jornada</p>
                Existen alquileres activos que deben ser completados antes de cerrar la jornada.
                <ul className="list-disc pl-5 mt-2">
                  {activeRentals.map((rental) => (
                    <li key={rental.id}>
                      {rental.customer?.name || 'N/A'} - Bici: #{rental.bike?.number || 'N/A'} - {rental.bike?.model || 'N/A'}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Resumen de la Jornada</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <DollarSign className="h-5 w-5 text-green-600 mr-2" />
                  <span>Total Recaudado</span>
                </div>
                <span className="font-bold text-green-600">${shift.total_collected?.toFixed(2) || '0.00'}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-blue-600 mr-2" />
                  <span>Alquileres</span>
                </div>
                <span className="font-bold">{shift.total_rentals}</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-blue-600 mr-2" />
                  <span>Alquileres Completados</span>
                </div>
                <span className="font-bold">{completedRentals.length}</span>
              </div>
              
              <hr />
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Percent className="h-5 w-5 text-orange-600 mr-2" />
                  <span>Parte del Operador ({operatorPercent}%)</span>
                </div>
                <div className="text-right">
                  <span className="font-bold text-orange-600">${shift.operator_amount?.toFixed(2) || '0.00'}</span>
                  {exchangeRate > 0 && (
                    <p className="text-xs text-muted-foreground">{operatorAmountInBs.toLocaleString('es-VE')} Bs</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Percent className="h-5 w-5 text-purple-600 mr-2" />
                  <span>Parte del Admin ({adminPercent}%)</span>
                </div>
                <div className="text-right">
                  <span className="font-bold text-purple-600">${shift.amount_admin?.toFixed(2) || '0.00'}</span>
                  {exchangeRate > 0 && (
                    <p className="text-xs text-muted-foreground">{adminAmountInBs.toLocaleString('es-VE')} Bs</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {showPaymentForm && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Información de Pago</CardTitle>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleCloseShift)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="bank_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Banco</FormLabel>
                          <Select 
                            onValueChange={(value) => field.onChange(Number(value))} 
                            value={field.value.toString()}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Seleccionar banco" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {banks.map((bank) => (
                                <SelectItem key={bank.id} value={bank.id.toString()}>
                                  {bank.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="amount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Monto (USD)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              {...field}
                              onChange={(e) => {
                                const value = e.target.value.replace(/(\.\d{2})\d+/, '$1');
                                field.onChange(value === '' ? 0 : Number(value));
                              }}
                            />
                          </FormControl>
                          {watchedAmount > 0 && exchangeRate > 0 && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {amountInBs.toLocaleString('es-VE')} Bs. (Tasa: {exchangeRate.toFixed(2)})
                            </p>
                          )}
                          {watchedAmount > shift.amount_admin && (
                            <p className="text-sm font-medium text-amber-600 mt-1">
                              ⚠️ Advertencia: El monto ingresado (${watchedAmount.toFixed(2)}) es mayor que el saldo pendiente (${shift.amount_admin.toFixed(2)}).
                            </p>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="reference"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Referencia</FormLabel>
                          <FormControl>
                            <Input placeholder="Número de referencia" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="space-y-2">
                      <FileUpload
                        label="Comprobante de Pago"
                        onFileSelect={setReceiptFile}
                        onFileRemove={() => setReceiptFile(null)}
                        selectedFile={receiptFile}
                        accept="image/*"
                        maxSize={5}
                      />
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isClosing}
            >
              Cancelar
            </Button>
            {!showPaymentForm ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowPaymentForm(true)}
                  disabled={isClosing || hasActiveRentals}
                >
                  Agregar Pago
                </Button>
                <Button
                  onClick={() => handleCloseShift()}
                  disabled={isClosing || hasActiveRentals}
                >
                  {isClosing ? "Cerrando..." : "Cerrar Sin Pago"}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowPaymentForm(false)}
                  disabled={isClosing}
                >
                  Volver
                </Button>
                <Button
                  onClick={form.handleSubmit(handleCloseShift)}
                  disabled={isClosing || hasActiveRentals}
                >
                  {isClosing ? "Cerrando..." : "Cerrar Con Pago"}
                </Button>
              </>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CloseShiftModal;